"use client";

import { useIMQA } from "@/components/imqa-provider";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import Link from "next/link";
import {
  AlertCircle,
  Globe,
  RefreshCw,
  ArrowRight,
  BadgeInfo,
  Terminal,
  UserCircle,
  Globe2,
  Play,
  Square,
  Smartphone
} from "lucide-react";
import type { IMQA } from "@imqa/web-agent"

interface ButtonGroup {
  title: string;
  buttons: {
    label: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    link?: string;
    loading?: boolean;
  }[];
}
export function TestButtons() {
  const { isInitialized } = useIMQA();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({});
  const [IMQA, setIMQA] = useState<IMQA | null>(null);
  const [userId, setUserId] = useState<string>("sample-user-id");
  const [globalAttributes, setGlobalAttributes] = useState<string>('{"hello":"world"}');
  const [activeUserEventId, setActiveUserEventId] = useState<string | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(null);

  // Import IMQA dynamically when needed
  const getIMQA = async () => {
    if (IMQA) return IMQA;
    
    try {
      const IMQAModule = await import('@imqa/web-agent');
      setIMQA(IMQAModule.default);
      return IMQAModule.default;
    } catch (error) {
      console.error('Failed to import IMQA:', error);
      return null;
    }
  };

  const handleAddAction = async () => {
    if (!isInitialized) {
      toast({
        title: "Agent not active",
        description: "Please activate the IMQA Web Agent first",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading((prev) => ({ ...prev, addAction: true }));
    
    try {
      const IMQA = await getIMQA();
      if (!IMQA) throw new Error("IMQA not available");
      
      IMQA.addAction('Test-Action', {
        actionType: 'button-click',
        actionName: 'Add Action Test',
        timestamp: Date.now(),
      });
      
      toast({
        title: "Action Added",
        description: "Custom action has been added to IMQA tracking",
      });
    } catch (error) {
      console.error('Failed to add action:', error);
      toast({
        title: "Action Failed",
        description: "Failed to add custom action to IMQA tracking",
        variant: "destructive",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, addAction: false }));
    }
  };

  const handleRecordLog = async () => {
    if (!isInitialized) {
      toast({
        title: "Agent not active",
        description: "Please activate the IMQA Web Agent first",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading((prev) => ({ ...prev, recordLog: true }));
    
    try {
      const IMQA = await getIMQA();
      if (!IMQA) throw new Error("IMQA not available");
      
      IMQA.recordLog('Test log message', {
        logLevel: 'info',
        source: 'test-button',
        timestamp: new Date().toLocaleString(),
      });
      
      toast({
        title: "Log Recorded",
        description: "Custom log has been recorded to IMQA tracking",
      });
    } catch (error) {
      console.error('Failed to record log:', error);
      toast({
        title: "Log Recording Failed",
        description: "Failed to record custom log to IMQA tracking",
        variant: "destructive",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, recordLog: false }));
    }
  };

  const handleConsoleError = () => {
    if (!isInitialized) {
      toast({
        title: "Agent not active",
        description: "Please activate the IMQA Web Agent first",
        variant: "destructive",
      });
      return;
    }
    
    console.error("This is a test console error");
    
    toast({
      title: "Console Error Generated",
      description: "A test console error has been logged",
    });
  };

  const handleTriggerError = () => {
    if (!isInitialized) {
      toast({
        title: "Agent not active",
        description: "Please activate the IMQA Web Agent first",
        variant: "destructive",
      });
      return;
    }
    
    try {
      // Intentionally cause an error
      const obj: any = null;
      obj.nonExistentMethod();
    } catch (error) {
      toast({
        title: "Error Triggered",
        description: "A JavaScript error has been triggered for tracking",
      });
    }
  };

  const handleSetUserId = async () => {
    if (!isInitialized) {
      toast({
        title: "Agent not active",
        description: "Please activate the IMQA Web Agent first",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading((prev) => ({ ...prev, setUserId: true }));
    
    try {
      const IMQA = await getIMQA();
      if (!IMQA) throw new Error("IMQA not available");
      
      IMQA.setUserIdAttribute(userId);
      
      toast({
        title: "User ID Set",
        description: `User ID has been set to "${userId}"`,
      });
    } catch (error) {
      console.error('Failed to set user ID:', error);
      toast({
        title: "Failed to Set User ID",
        description: "An error occurred while setting the user ID",
        variant: "destructive",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, setUserId: false }));
    }
  };

  const handleGetDeviceId = async () => {
    setIsLoading((prev) => ({ ...prev, getDeviceId: true }));

    try {
      const IMQA = await getIMQA();
      if (!IMQA) throw new Error("IMQA not available");

      const id = IMQA.getDeviceId();
      setDeviceId(id);

      toast({
        title: "Device ID Retrieved",
        description: id ? `Device ID: ${id}` : "Device ID not available (null)",
      });
    } catch (error) {
      console.error('Failed to get device ID:', error);
      setDeviceId(null);
      toast({
        title: "Failed to Get Device ID",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, getDeviceId: false }));
    }
  };

  const handleSetGlobalAttributes = async () => {
    if (!isInitialized) {
      toast({
        title: "Agent not active",
        description: "Please activate the IMQA Web Agent first",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading((prev) => ({ ...prev, setGlobalAttributes: true }));
    
    try {
      const IMQA = await getIMQA();
      if (!IMQA) throw new Error("IMQA not available");
      
      let attributes;
      try {
        attributes = JSON.parse(globalAttributes);
      } catch (e) {
        throw new Error("Invalid JSON format for global attributes");
      }
      
      IMQA.setGlobalAttributes(attributes);
      
      toast({
        title: "Global Attributes Set",
        description: "Global attributes have been set successfully",
      });
    } catch (error) {
      console.error('Failed to set global attributes:', error);
      toast({
        title: "Failed to Set Global Attributes",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, setGlobalAttributes: false }));
    }
  };

  // User Event handlers
  const handleStartQuickUserEvent = async () => {
    if (!isInitialized) {
      toast({
        title: "Agent not active",
        description: "Please activate the IMQA Web Agent first",
        variant: "destructive",
      });
      return;
    }

    if (activeUserEventId) {
      toast({
        title: "Event already active",
        description: "Please end the current event before starting a new one",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading((prev) => ({ ...prev, startUserEvent: true }));
    
    try {
      const IMQA = await getIMQA();
      if (!IMQA) throw new Error("IMQA not available");
      
      const eventId = IMQA.userEvent.start('quick-test-event', {
        'test.source': 'test-buttons',
        'test.timestamp': Date.now(),
      });
      
      setActiveUserEventId(eventId);
      
      toast({
        title: "User Event Started",
        description: `Quick test event started: ${eventId.slice(0, 8)}...`,
      });
    } catch (error) {
      console.error('Failed to start user event:', error);
      toast({
        title: "Start Failed",
        description: error instanceof Error ? error.message : "Failed to start user event",
        variant: "destructive",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, startUserEvent: false }));
    }
  };

  const handleEndQuickUserEvent = async () => {
    if (!isInitialized) {
      toast({
        title: "Agent not active",
        description: "Please activate the IMQA Web Agent first",
        variant: "destructive",
      });
      return;
    }

    if (!activeUserEventId) {
      toast({
        title: "No active event",
        description: "No user event is currently active",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading((prev) => ({ ...prev, endUserEvent: true }));
    
    try {
      const IMQA = await getIMQA();
      if (!IMQA) throw new Error("IMQA not available");
      
      IMQA.userEvent.end(activeUserEventId, {
        'test.result': 'completed',
        'test.duration': Date.now(),
      });
      
      toast({
        title: "User Event Ended",
        description: `Event ended: ${activeUserEventId.slice(0, 8)}...`,
      });
      
      setActiveUserEventId(null);
    } catch (error) {
      console.error('Failed to end user event:', error);
      toast({
        title: "End Failed",
        description: error instanceof Error ? error.message : "Failed to end user event",
        variant: "destructive",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, endUserEvent: false }));
    }
  };

  const buttonGroups: ButtonGroup[] = [
    {
      title: "User Event Testing",
      buttons: [
        {
          label: activeUserEventId ? "Event Active" : "Start Quick Event",
          icon: <Play className="mr-2 h-4 w-4" />,
          onClick: handleStartQuickUserEvent,
          loading: isLoading.startUserEvent,
        },
        {
          label: "End Current Event",
          icon: <Square className="mr-2 h-4 w-4" />,
          onClick: handleEndQuickUserEvent,
          loading: isLoading.endUserEvent,
        },
        {
          link: "/user-event-test",
          label: "Go to User Event Test Page",
          icon: <ArrowRight className="mr-2 h-4 w-4" />,
        },
      ],
    },
    {
      title: "Custom Tracking Actions",
      buttons: [
        {
          label: "Add Custom Action",
          icon: <BadgeInfo className="mr-2 h-4 w-4" />,
          onClick: handleAddAction,
          loading: isLoading.addAction,
        },
        {
          label: "Record Custom Log",
          icon: <Terminal className="mr-2 h-4 w-4" />,
          onClick: handleRecordLog,
          loading: isLoading.recordLog,
        },
        {
          label: "Set User ID",
          icon: <UserCircle className="mr-2 h-4 w-4" />,
          onClick: handleSetUserId,
          loading: isLoading.setUserId,
        },
        {
          label: "Set Global Attributes",
          icon: <Globe2 className="mr-2 h-4 w-4" />,
          onClick: handleSetGlobalAttributes,
          loading: isLoading.setGlobalAttributes,
        },
        {
          label: "Get Device ID",
          icon: <Smartphone className="mr-2 h-4 w-4" />,
          onClick: handleGetDeviceId,
          loading: isLoading.getDeviceId,
        },
      ],
    },
    {
      title: "Error Testing",
      buttons: [
        {
          label: "Generate Console Error",
          icon: <AlertCircle className="mr-2 h-4 w-4" />,
          onClick: handleConsoleError,
        },
        {
          label: "Trigger JS Error",
          icon: <AlertCircle className="mr-2 h-4 w-4" />,
          onClick: handleTriggerError,
        },
        {
          link: "/error-test",
          label: "Go to Error Test Page",
          icon: <ArrowRight className="mr-2 h-4 w-4" />,
        },
      ],
    },
    {
      title: "Network Testing",
      buttons: [
        {
          link: "/network-test",
          label: "Go to Network Test Page",
          icon: <Globe className="mr-2 h-4 w-4" />,
        },
      ],
    },
    {
      title: "Route Change Testing",
      buttons: [
        {
          link: "/route-test-1",
          label: "Go to Route Test Page 1",
          icon: <RefreshCw className="mr-2 h-4 w-4" />,
        },
        {
          link: "/route-test-2",
          label: "Go to Route Test Page 2",
          icon: <RefreshCw className="mr-2 h-4 w-4" />,
        },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      {deviceId !== null && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">Device ID</h3>
          <p className="font-mono text-sm break-all">{deviceId}</p>
        </div>
      )}

      <div className="space-y-3">
        <h3 className="text-lg font-medium">Input Values</h3>
        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
            <label htmlFor="userId" className="text-sm font-medium">
              User ID:
            </label>
            <input
              id="userId"
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="w-full p-2 border rounded-md"
              disabled={!isInitialized}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="globalAttributes" className="text-sm font-medium">
              Global Attributes (JSON):
            </label>
            <textarea
              id="globalAttributes"
              value={globalAttributes}
              onChange={(e) => setGlobalAttributes(e.target.value)}
              className="w-full p-2 border rounded-md min-h-[100px]"
              disabled={!isInitialized}
            />
          </div>
        </div>
      </div>
      
      {buttonGroups.map((group, groupIndex) => (
        <div key={groupIndex} className="space-y-3">
          <h3 className="text-lg font-medium">{group.title}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {group.buttons.map((button, buttonIndex) => 
              button.link ? (
                <Link href={button.link} key={buttonIndex}>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    disabled={!isInitialized}
                  >
                    {button.icon}
                    {button.label}
                  </Button>
                </Link>
              ) : (
                <Button 
                  key={buttonIndex}
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={button.onClick}
                  disabled={!isInitialized || button.loading}
                >
                  {button.icon}
                  {button.loading ? "Loading..." : button.label}
                </Button>
              )
            )}
          </div>
        </div>
      ))}
    </div>
  );
}